import { ofType as rxOfType, Epic } from 'redux-observable';
import { catchError, mergeMap, debounceTime, takeUntil, withLatestFrom } from 'rxjs/operators';
import { TrackEvent } from '@alycecom/services';
import { ofType } from '@alycecom/utils';
import { IOption } from '@alycecom/ui';

import { getHashId, getIsDonation } from '../settings/settings.selectors';

import {
  loadProductsSuccess,
  loadProductsFail,
  setFilter,
  setSorting,
  setPage,
  setSearch,
  resetProducts,
  resetFilters,
  setCountryId,
} from './products.actions';
import { getPagination, getFilters, getSorting, getSearch, getCountryId } from './products.selectors';
import { IFilters, IPagination, ISort } from './products.types';

const getMarketplaceProductsUrl = (hashId: string, isDonation: boolean = false) => {
  const marketplacePrefix = isDonation ? '/donation/' : '/';
  return `/api/recipient/${hashId}/marketplace${marketplacePrefix}products`;
};

const getFilterIds = (filters: IOption[]) => filters.map(item => item.id);

const getProductsRequestParams = (
  pagination: IPagination,
  search: string,
  sort: ISort,
  filters: IFilters,
  countryId?: number,
) => ({
  search,
  page: pagination.currentPage,
  sort: sort.value,
  type: getFilterIds(filters.giftTypes),
  category: getFilterIds(filters.categories),
  merchant: getFilterIds(filters.brands),
  country_id: countryId,
});

export const filterProductsEpic: Epic = action$ =>
  action$.pipe(
    rxOfType(...[resetFilters, setFilter, setSorting, setCountryId, setSearch]),
    mergeMap(() => [resetProducts(), setPage(1)]),
  );

export const loadProductsEpic: Epic = (action$, state$, { apiService, messagesService: { errorHandler } }) =>
  action$.pipe(
    ofType(setPage),
    debounceTime(500),
    withLatestFrom(state$),
    mergeMap(([, state]) => {
      const hashId = getHashId(state);
      const isDonation = getIsDonation(state);
      const pagination = getPagination(state);
      const search = getSearch(state);
      const sort = getSorting(state);
      const filters = getFilters(state);
      const countryId = getCountryId(state);

      const requestParams = getProductsRequestParams(pagination, search, sort, filters, countryId);

      return apiService.post(getMarketplaceProductsUrl(hashId, isDonation), { body: requestParams }).pipe(
        mergeMap(({ products, meta }) => [
          loadProductsSuccess({ products, pagination: meta.pagination }),
          TrackEvent.actions.trackEvent({
            name: 'Marketplace Products - Loaded',
            payload: { hashId, filters: requestParams, page: requestParams.page, total: meta.pagination.total },
          }),
        ]),
        catchError(errorHandler({ callbacks: loadProductsFail })),
        takeUntil(action$.ofType(setSearch, setFilter, setSorting)),
      );
    }),
  );

export const productsEpics = [filterProductsEpic, loadProductsEpic];
